import RgbColor from "../../entities/RgbColor";

export default class GoogleSlidesJsonGenerationService {
	static createSlide(slideId: string) {
		return {
			createSlide: {
				objectId: slideId,
			},
		};
	}

	static createShape(shapeId: string, pageId: string, type: string, height: number, width: number) {
		return {
			createShape: {
				objectId: shapeId,
				elementProperties: {
					pageObjectId: pageId,
					size: {
						width: {
							magnitude: width,
							unit: "PT",
						},
						height: {
							magnitude: height,
							unit: "PT",
						},
					},
					transform: {
						scaleX: 1,
						scaleY: 1,
						shearX: 0,
						shearY: 0,
						translateX: 36,
						translateY: 1,
						unit: "PT",
					},
				},
				shapeType: type,
			},
		};
	}

	static createImage(imgId: string, pageId: string, imageUrl: string) {
		return {
			createImage: {
				objectId: imgId,
				elementProperties: {
					pageObjectId: pageId,
				},
				url: imageUrl,
			},
		};
	}

	static createUpdateTextBackgroundRequest(textBoxId: string, endIndex: number, startIndex: number, backgroundColor: RgbColor) {
		const convertToOnesScale = 0.003921568627451;
		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					backgroundColor: {
						opaqueColor: {
							rgbColor: {
								red: backgroundColor.r * convertToOnesScale,
								green: backgroundColor.g * convertToOnesScale,
								blue: backgroundColor.b * convertToOnesScale,
							},
						},
					},
				},
				textRange: {
					startIndex,
					endIndex: endIndex + 1,
					type: "FIXED_RANGE",
				},
				fields: "backgroundColor",
			},
		};
	}

	static createUpdateFontFamilyRequest(textBoxId: string, endIndex: number, startIndex: number, fontFamily: string) {
		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					fontFamily,
				},
				textRange: {
					startIndex,
					endIndex: endIndex + 1,
					type: "FIXED_RANGE",
				},
				fields: "fontFamily",
			},
		};
	}

	static createListMarkerRequest(textBoxId: string, endIndex: number, startIndex: number, type: "ordered" | "unordered") {
		return {
			createParagraphBullets: {
				objectId: textBoxId,
				textRange: {
					startIndex,
					endIndex: endIndex + 1,
					type: "FIXED_RANGE",
				},
				bulletPreset: type === "ordered" ? "NUMBERED_DIGIT_NESTED" : "BULLET_DISC_CIRCLE_SQUARE",
			},
		};
	}

	static centerTextVerticle(textBoxId: string) {
		return {
			updateShapeProperties: {
				objectId: textBoxId,
				shapeProperties: {
					contentAlignment: "MIDDLE",
				},
				fields: "contentAlignment",
			},
		};
	}

	static centerTextHorizontal(textBoxId: string) {
		return {
			updateParagraphStyle: {
				objectId: textBoxId,
				style: {
					alignment: "CENTER",
				},
				fields: "alignment",
			},
		};
	}

	static changeFontColor(textBoxId: string, color: string) {
		const whiteFont = {
			red: 1,
			green: 1,
			blue: 1,
		};
		const blackFont = {
			red: 0,
			green: 0,
			blue: 0,
		};

		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					foregroundColor: {
						opaqueColor: {
							rgbColor: color === "white" ? whiteFont : blackFont,
						},
					},
				},
				textRange: {
					type: "ALL",
				},
				fields: "foregroundColor",
			},
		};
	}

	static changeFontSize(textBoxId: string, endIndex: number | undefined, startIndex: number | undefined, magnitude: number) {
		if (startIndex !== undefined && endIndex !== undefined) {
			return {
				updateTextStyle: {
					objectId: textBoxId,
					style: {
						fontSize: {
							magnitude: magnitude,
							unit: "PT",
						},
					},
					textRange: {
						startIndex,
						endIndex: endIndex + 1,
						type: "FIXED_RANGE",
					},
					fields: "fontSize",
				},
			};
		}

		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					fontSize: {
						magnitude: magnitude,
						unit: "PT",
					},
				},
				textRange: {
					type: "ALL",
				},
				fields: "fontSize",
			},
		};
	}

	static makeFontBold(textBoxId: string, startIndex: number, endIndex: number) {
		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					bold: true,
				},
				textRange: {
					startIndex,
					endIndex: endIndex + 1,
					type: "FIXED_RANGE",
				},
				fields: "bold",
			},
		};
	}

	static makeFontItalics(textBoxId: string, startIndex: number, endIndex: number) {
		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					italic: true,
				},
				textRange: {
					startIndex,
					endIndex: endIndex + 1,
					type: "FIXED_RANGE",
				},
				fields: "italic",
			},
		};
	}

	static makeFontUnderlined(textBoxId: string, startIndex: number, endIndex: number) {
		return {
			updateTextStyle: {
				objectId: textBoxId,
				style: {
					underline: true,
				},
				textRange: {
					startIndex,
					endIndex: endIndex + 1,
					type: "FIXED_RANGE",
				},
				fields: "underline",
			},
		};
	}

	static makeTextIndented(textBoxId: string, startIndex: number | undefined, endIndex: number | undefined, magnitude: number) {
		if (startIndex === undefined || endIndex === undefined) {
			return {
				updateParagraphStyle: {
					objectId: textBoxId,
					style: {
						indentStart: {
							magnitude,
							unit: "PT",
						},
					},
					textRange: {
						type: "ALL",
					},
					fields: "indentStart",
				},
			};
		}

		return {
			updateParagraphStyle: {
				objectId: textBoxId,
				style: {
					indentStart: {
						magnitude,
						unit: "PT",
					},
				},
				textRange: {
					startIndex,
					endIndex,
					type: "FIXED_RANGE",
				},
				fields: "indentStart",
			},
		};
	}

	static makeTextFirstLineIndented(textBoxId: string, startIndex: number | undefined, endIndex: number | undefined, magnitude: number) {
		if (startIndex === undefined || endIndex === undefined) {
			return {
				updateParagraphStyle: {
					objectId: textBoxId,
					style: {
						indentStart: {
							magnitude,
							unit: "PT",
						},
					},
					textRange: {
						type: "ALL",
					},
					fields: "indentStart",
				},
			};
		}

		return {
			updateParagraphStyle: {
				objectId: textBoxId,
				style: {
					indentFirstLine: {
						magnitude,
						unit: "PT",
					},
				},
				textRange: {
					startIndex,
					endIndex,
					type: "FIXED_RANGE",
				},
				fields: "indentFirstLine",
			},
		};
	}

	static makeTextAligned(textBoxId: string, alignment: string) {
		return {
			updateParagraphStyle: {
				objectId: textBoxId,
				style: {
					alignment,
				},
				textRange: {
					type: "ALL",
				},
				fields: "alignment",
			},
		};
	}

	static sendElementToBack(objectIds: string[]) {
		return {
			updatePageElementsZOrder: {
				pageElementObjectIds: [...objectIds],
				operation: "SEND_TO_BACK",
			},
		};
	}

	static insertImageAsStretchedBackground(pageId: string, imageUrl: string) {
		return {
			updatePageProperties: {
				objectId: pageId,
				pageProperties: {
					pageBackgroundFill: {
						stretchedPictureFill: {
							contentUrl: imageUrl,
						},
					},
				},
				fields: "pageBackgroundFill.stretchedPictureFill.contentUrl",
			},
		};
	}

	static insertText(text: string, containerId: string) {
		return {
			insertText: {
				objectId: containerId,
				text: text,
			},
		};
	}

	static deleteTitlePage() {
		return {
			deleteObject: {
				objectId: "p",
			},
		};
	}
}
